import React, { useEffect, useMemo, useRef, useState } from 'react';

// Minimal, controlled editor for gallery item structure
// Schema:
// {
//   columns: [
//     { properties: [ { name: string, type: 'image'|'text'|'line', required: bool, show_name: bool, meta: { prefix?: string, suffix?: string } } ] }
//   ]
// }
// Props:
// - value: current structure object (optional)
// - onChange: function(structure)
// - className: optional
const DEFAULT_PROP = { name: '', type: 'line', required: false, show_name: true, meta: { prefix: '', suffix: '', fontSize: 'text' } };

function normalize(struct) {
  const base = struct && typeof struct === 'object' ? struct : {};
  const cols = Array.isArray(base.columns) ? base.columns : [];
  const normalizedCols = cols.map((col) => ({
    properties: Array.isArray(col?.properties)
      ? col.properties.map((p) => {
          const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : 'text';

          // meta is only supported for type 'line'. For other types meta MUST be null.
          let meta = null;
          if (type === 'line') {
            const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
            const allowedFontSizes = ['heading', 'sub-heading', 'text'];
            const fs = String(m.fontSize ?? '').toLowerCase();
            meta = {
              prefix: String(m.prefix ?? ''),
              suffix: String(m.suffix ?? ''),
              fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
            };
          }

          return {
            name: String(p?.name ?? ''),
            type,
            required: !!p?.required,
            show_name: p?.show_name === undefined ? true : !!p.show_name,
            meta,
          };
        })
      : [],
  }));
  return { columns: normalizedCols };
}

const GalleryItemStructureEditor = ({ value, onChange, className = '' }) => {
  const struct = useMemo(() => normalize(value), [value]);

  const [collapsed, setCollapsed] = useState(false);
  const initialSetRef = useRef(false);

  // On first arrival of a non-empty structure (has at least one property), collapse by default
  useEffect(() => {
    if (initialSetRef.current) return;
    const hasAnyProperty = Array.isArray(struct.columns) && struct.columns.some(c => Array.isArray(c.properties) && c.properties.length > 0);
    if (hasAnyProperty) setCollapsed(true);
    initialSetRef.current = true;
  }, [struct]);

  const emit = (next) => {
    onChange?.(normalize(next));
  };

  const addColumn = () => {
    const next = { columns: [...struct.columns, { properties: [] }] };
    emit(next);
  };

  const removeColumn = (idx) => {
    const nextCols = struct.columns.filter((_, i) => i !== idx);
    emit({ columns: nextCols });
  };

  const addProperty = (cIdx) => {
    const nextCols = struct.columns.map((c, i) => (i === cIdx ? { properties: [...c.properties, { ...DEFAULT_PROP }] } : c));
    emit({ columns: nextCols });
  };

  const removeProperty = (cIdx, pIdx) => {
    const nextCols = struct.columns.map((c, i) =>
      i === cIdx ? { properties: c.properties.filter((_, j) => j !== pIdx) } : c
    );
    emit({ columns: nextCols });
  };

  const updateProperty = (cIdx, pIdx, field, val) => {
    const nextCols = struct.columns.map((c, i) => {
      if (i !== cIdx) return c;
      const props = c.properties.map((p, j) => {
        if (j !== pIdx) return p;
        // Special handling for type change to enforce meta rules
        if (field === 'type') {
          const nextType = String(val || '').toLowerCase();
          if (nextType === 'line') {
            return { ...p, type: 'line', meta: p.meta && typeof p.meta === 'object'
                    ? { prefix: p.meta.prefix || '', suffix: p.meta.suffix || '', fontSize: p.meta.fontSize || 'text' }
                    : { prefix: '', suffix: '', fontSize: 'text' } };
          }
          return { ...p, type: nextType || 'text', meta: null };
        }
        return { ...p, [field]: val };
      });
      return { properties: props };
    });
    emit({ columns: nextCols });
  };

  const moveProperty = (cIdx, pIdx, dir) => {
    const props = [...(struct.columns[cIdx]?.properties || [])];
    const to = pIdx + dir;
    if (to < 0 || to >= props.length) return;
    const tmp = props[pIdx];
    props[pIdx] = props[to];
    props[to] = tmp;
    const nextCols = struct.columns.map((c, i) => (i === cIdx ? { properties: props } : c));
    emit({ columns: nextCols });
  };

  const containerStyle = { border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, margin: '12px 0' };
  const columnStyle = { border: '1px dashed #cbd5e1', borderRadius: 8, padding: 12, marginBottom: 12 };
  const headerCell = { fontSize: 12, color: '#64748b' };
// ... existing code ...
  return (
    <div className={className}>
      <style>{`
        .gis-row {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr 0.6fr 0.8fr 1fr 1fr 0.8fr auto;
          gap: 8px;
          align-items: center;
        }
        .gis-actions {
          display: flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .gis-header-row .gis-actions {
          display: none;
        }
        @media (max-width: 640px) {
          .gis-row {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            align-items: stretch;
          }
          .gis-header-row {
            display: none;
          }
          .gis-actions {
            grid-column: 1 / -1;
            justify-content: flex-end;
          }
        }
      `}</style>
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong>Gallery item structure</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="primary-button" onClick={() => setCollapsed(c => !c)}>{collapsed ? 'Expand' : 'Collapse'}</button>
            <button type="button" className="primary-button" onClick={addColumn}>Add column</button>
          </div>
        </div>

        {collapsed ? (
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>
            {(() => {
              const totalCols = struct.columns?.length || 0;
              const totalProps = (struct.columns || []).reduce((acc, c) => acc + ((c.properties || []).length), 0);
              return `${totalCols} column${totalCols !== 1 ? 's' : ''}, ${totalProps} propert${totalProps === 1 ? 'y' : 'ies'}`;
            })()}
          </div>
        ) : (
          <>
            {struct.columns.length === 0 && (
              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>No columns yet. Add the first column.</div>
            )}

            {struct.columns.map((col, cIdx) => (
              <div key={cIdx} style={columnStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>Column #{cIdx + 1}</div>
                  <button type="button" className="primary-button" onClick={() => removeColumn(cIdx)}>Remove column</button>
                </div>

                {(col.properties || []).map((p, pIdx) => (
                  <div key={pIdx} className="gis-row" style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <input
                              type="checkbox"
                              checked={!!p.required}
                              onChange={(e) => updateProperty(cIdx, pIdx, 'required', e.target.checked)}
                              aria-label="Required"
                              title={'required'}
                          />
                      </div>
                    <input
                      type="text"
                      className="form-control"
                      value={p.name}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'name', e.target.value)}
                      placeholder="Property name"
                    />
                    <select
                      className="form-control"
                      value={p.type}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'type', e.target.value)}
                    >
                      <option value="line">line</option>
                      <option value="text">text</option>
                      <option value="image">image</option>
                    </select>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!p.show_name}
                        onChange={(e) => updateProperty(cIdx, pIdx, 'show_name', e.target.checked)}
                        aria-label="Show name"
                      />
                    </div>
                    {p.type === 'line' ? (
                      <>
                        <input
                          type="text"
                          className="form-control"
                          value={p.meta?.prefix || ''}
                          onChange={(e) =>
                            updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), prefix: e.target.value })
                          }
                          placeholder="Prefix"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={p.meta?.suffix || ''}
                          onChange={(e) =>
                            updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), suffix: e.target.value })
                          }
                          placeholder="Suffix"
                        />
                        <select
                          className="form-control"
                          value={p.meta?.fontSize || 'text'}
                          onChange={(e) =>
                            updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), fontSize: e.target.value })
                          }
                          aria-label="Font size"
                          title="Font size"
                        >
                          <option value="heading">heading</option>
                          <option value="sub-heading">sub-heading</option>
                          <option value="text">text</option>
                        </select>
                      </>
                    ) : (
                      <>
                        <div />
                        <div />
                        <div />
                      </>
                    )}
                    <div className="gis-actions">
                      <button type="button" className="primary-button" onClick={() => moveProperty(cIdx, pIdx, -1)} title="Move up">↑</button>
                      <button type="button" className="primary-button" onClick={() => moveProperty(cIdx, pIdx, 1)} title="Move down">↓</button>
                      <button type="button" className="primary-button" onClick={() => removeProperty(cIdx, pIdx)}>Remove</button>
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: 8 }}>
                  <button type="button" className="primary-button" onClick={() => addProperty(cIdx)}>Add property</button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default GalleryItemStructureEditor;